import { Box } from '@breathelife/mui';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Styles/themed-styled-components';

import { FullColorGradient, NavigationSidebarVariants, Permission } from '@breathelife/types';

import { AdminIcon } from '../../Assets/Icons/AdminIcon';
import { CustomersIcon } from '../../Assets/Icons/CustomersIcon';
import { Icon } from '../../Components/Icons';
import { RestrictedToUserPermissions } from '../Restricted/RestrictedToUserPermissions';
import { useCarrierContext } from '../../Hooks';
import Urls from '../../Navigation/Urls';
import { LeadPlatformTheme } from '../../Styles/Types';
import { SidebarNavLink } from './SidebarNavLink';
import { SidebarFooter, StyledSidebar } from './Styles';

type VariantStyle = {
  background: string;
  border: string;
  grey: keyof FullColorGradient;
  hoverColor: string;
  activeColor: string;
};

export const handleColorsVariant = (theme: LeadPlatformTheme, variant?: NavigationSidebarVariants): VariantStyle => {
  let backgroundColor1 = theme.colors.primary[60];
  let backgroundColor2 = theme.colors.primary[70];
  let border = `1px solid ${theme.colors.primary[70]};`;
  let hoverColor = theme.colors.primary[70];
  let activeColor = theme.colors.primary[80];
  let grey: keyof FullColorGradient = 0;

  switch (variant) {
    case NavigationSidebarVariants.light:
      backgroundColor1 = theme.colors.grey[0];
      backgroundColor2 = theme.colors.grey[10];
      border = `1px solid ${theme.colors.grey[40]}`;
      hoverColor = theme.colors.grey[20];
      activeColor = theme.colors.grey[30];
      grey = 100;
      break;
    case NavigationSidebarVariants.primary:
    case NavigationSidebarVariants.secondary:
      backgroundColor1 = theme.colors[variant][60];
      backgroundColor2 = theme.colors[variant][70];
      border = `1px solid ${theme.colors[variant][70]};`;
      hoverColor = theme.colors[variant][70];
      activeColor = theme.colors[variant][80];
      break;
  }

  const background = `linear-gradient(
    160deg,
    ${backgroundColor1} 0%,
    ${backgroundColor2} 150%
  )`;

  return {
    background,
    border,
    grey,
    hoverColor,
    activeColor,
  };
};

type SideBarLink = {
  id: string;
  url: string;
  text: string;
  icon: ReactElement;
  requiredPermissions?: Permission[];
};
type SidebarProps = {
  logo: ReactElement;
};

export function Sidebar(props: SidebarProps): ReactElement {
  const { features, navigationSidebarVariant } = useCarrierContext();
  const theme = useTheme();
  const styles = handleColorsVariant(theme, navigationSidebarVariant);
  const { t } = useTranslation();
  const softwareByTitle = t('softwareByBreatheLife');

  const sideBarLinks: SideBarLink[] = [
    {
      id: 'leads',
      url: Urls.leads.fullPath,
      text: features.leadCreation.enabled ? t('sidebar.customers') : t('sidebar.applications'),
      icon: <CustomersIcon stroke={theme.colors.grey[styles.grey]} />,
    },
    {
      id: 'admin',
      url: Urls.admin.fullPath,
      text: t('sidebar.admin'),
      icon: <AdminIcon stroke={theme.colors.grey[styles.grey]} />,
      requiredPermissions: [Permission.AdminToolsRead],
    },
  ];

  return (
    <StyledSidebar background={styles.background} border={styles.border} role='complementary'>
      <Box mt={3}>{props.logo}</Box>
      <Box flex='1' display='flex' flexDirection='column' justifyContent='space-between' alignItems='center'>
        <Box flex='1' display='flex' flexDirection='column' alignItems='center'>
          {sideBarLinks.map((navLink) => {
            return (
              <RestrictedToUserPermissions key={navLink.id} requiredPermissions={navLink.requiredPermissions}>
                <SidebarNavLink
                  to={navLink.url}
                  text={navLink.text}
                  icon={navLink.icon}
                  greyShade={styles.grey}
                  activeColor={styles.activeColor}
                  hoverColor={styles.hoverColor}
                />
              </RestrictedToUserPermissions>
            );
          })}
        </Box>
        {features.softwareByWidget.enabled && (
          <SidebarFooter mb={2} pt={2} width='56px' display='flex' justifyContent='center'>
            <SoftwareByWrapper softwareByUrl={features.softwareByWidget.softwareByUrl} ariaLabel={softwareByTitle}>
              <Icon
                fill={theme.colors.grey[50]}
                name='breatheLifeLogoSmall'
                width='100%'
                height='17px'
                id='bl-logo-sidebar'
                title={softwareByTitle}
              />
            </SoftwareByWrapper>
          </SidebarFooter>
        )}
      </Box>
    </StyledSidebar>
  );
}

type SoftwareByWrapperProps = {
  children: ReactElement;
  ariaLabel: string;
  softwareByUrl?: string;
};

function SoftwareByWrapper(props: SoftwareByWrapperProps): ReactElement {
  const { children, softwareByUrl } = props;

  if (!softwareByUrl) return children;

  return (
    <a href={softwareByUrl} rel='noopener noreferrer' target='_blank'>
      {children}
    </a>
  );
}
